<template>
  <!-- prettier-ignore -->
  <ovp-modal
    v-model="modalActive"
    locked>
    <div class="auth">
      <div class="auth__title">{{ $t('screen.auth.title') }}</div>

      <template v-if="!requested">
        <div class="auth__description">{{ $t('screen.auth.request.description') }}</div>

        <div class="auth__sentTAN">{{ $t('screen.auth.recipient', { phone }) }}</div>

        <ovp-button
          class="auth__button"
          :disabled="smsDisabled"
          primary
          @click="sendTAN">
          {{ $t('screen.auth.request.requestTAN') }}
        </ovp-button>
      </template>

      <template v-else-if="requested">
        <div class="auth__description">{{ $t('screen.auth.confirm.description') }}</div>

        <ovp-form
          autocomplete="off"
          class="auth-form"
          name="auth"
          tracking-disabled
          :validator="$v.form"
          @submit="submit"
        >
          <ovp-code-input
            v-model="form.tan"
            autofocus
            numeric
            class="auth__input"
            mask="####"
            name="tan"
            theme="dark"
            :delay="0"
            :title="$t('screen.reentry.insertTan')"
            :validate="$v.form.tan"
          />

          <ovp-button
            primary
            type="submit">
            {{ $t('form.next') }}
          </ovp-button>

          <ovp-button
            class="auth__button"
            tiny
            :disabled="smsDisabled"
            plain
            @click="sendTAN">
            {{ $t('screen.auth.confirm.requestTAN') }}
          </ovp-button>

        </ovp-form>

      </template>

      <a
        class="auth__end"
        @click="endSession">
        {{ $t('screen.auth.end') }}
      </a>
    </div>
  </ovp-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { mapState } from 'vuex'

import OvpModal from '@/components/Modal/Modal'

import { ROUTE_NAME_NEXT_STEPS, SESSION_TIME_TO_AUTH, SESSION_TIME_TO_CLEAR } from '@/config/constants'
import { EVENT_APPLICATION_SUBMITTED, EVENT_TOKEN_UPDATE } from '@/config/events'
import { BasketHelper, CollectiveHelper, LogHelper, StatusHelper } from '@/helpers'
import BasketMixin from '@/mixins/Basket'

export default {
  name: 'Auth',

  mixins: [BasketMixin],

  components: {
    OvpModal,
  },

  data() {
    return {
      modalActive: false,
      requested: false,
      form: {
        tan: null,
      },
      smsDisabled: false,

      timerToAuth: false,
      timerToClear: false,
    }
  },

  validations: {
    form: {
      tan: {
        required,
      },
    },
  },

  mounted() {
    // delay, leave time for the computeds to process
    window.setTimeout(() => this.on(), 5000)
    this.$EventBus.$on(EVENT_APPLICATION_SUBMITTED, () => {
      this.stopTimer()
      this.off()
    })
  },

  beforeDestroy() {
    this.off()
  },

  computed: {
    ...mapState(['basket']),
    ...mapState('basket', ['mobile']),

    phone() {
      if (!this.mobile) return

      const start = this.mobile.substr(0, 5)
      const inbetween = this.mobile.substring(5, this.mobile.length - 2).replace(/\d/g, '*')
      const end = this.mobile.substr(-2)

      return `${start}${inbetween}${end}`
    },
  },

  methods: {
    closeModal() {
      this.modalActive = false
    },

    endSession() {
      this.$session.clear().then(() => {
        this.closeModal()
        window.location.reload()
      })
    },

    off() {
      this.$EventBus.$off(EVENT_TOKEN_UPDATE, this.reset)
    },

    on() {
      this.$EventBus.$on(EVENT_TOKEN_UPDATE, this.reset)
    },

    reset() {
      if (this.basket.submitted) {
        this.stopTimer()
        this.off()
      }

      // if onboarding-secured and basket not yet submitted, register notification
      if (StatusHelper.isOnboardingSecured(this.basket) && !this.basket.submitted) {
        this.stopTimer()

        this.timerToAuth = window.setTimeout(this.showAuthForm, SESSION_TIME_TO_AUTH)
      }
    },

    sendTAN() {
      if (this.requested) this.smsDisabled = true
      this.requested = true

      this.stopTimer()

      this.$backend.ovp
        .sendSMSTan(this.basket.reentryToken)
        .then(() => {
          this.$notification.add(this.$t('screen.auth.sent'))
        })
        .finally(() => {
          // retrying for a tan is disabled for 2
          window.setTimeout(() => (this.smsDisabled = false), 60 * 2 * 1000)
        })
    },

    /**
     * show auth-form modal
     *
     * turn off listener
     * start timer for session-ender
     *
     */
    showAuthForm() {
      // cancel events, as long as form is shown
      this.off()
      this.timerToClear = window.setTimeout(() => this.endSession(), SESSION_TIME_TO_CLEAR)
      this.modalActive = true
    },

    stopTimer() {
      if (this.timerToAuth) window.clearTimeout(this.timerToAuth)
      if (this.timerToClear) window.clearTimeout(this.timerToClear)
    },

    /**
     * submit tan
     *
     * when response is submitted => redirect to nextSteps
     * when response is !submitted => turn on listener
     */
    submit() {
      // clear timeouts
      this.stopTimer()

      const payload = {
        basketId: this.basket.basketId,
        tan: this.form.tan,
        token: this.basket.reentryToken,
      }

      this.$backend.ovp
        .loadBasket(payload)
        .then(basket => BasketHelper.setLanguage(basket))
        .then(basket => BasketHelper.updateBasketSession(basket))
        .then(basket => CollectiveHelper.loadCollective(basket))
        .then(basket => BasketHelper.getAndSetProducts(basket))
        .then(basket => BasketHelper.getAndSetDistributionPartnerByBasket(basket))
        .then(basket => {
          this.form.tan = null
          this.requested = false
          this.$v.$reset()

          if (basket.submitted) {
            this.$router.push({ name: ROUTE_NAME_NEXT_STEPS })
          } else {
            this.on()
            this.reset()
          }

          this.closeModal()
        })
        .catch(LogHelper.error)
    },
  },
}
</script>

<style name="mobile" scoped>
.auth {
  color: var(--c-black);
  font-size: 15px;
  line-height: 20px;
  padding: 20px;

  &__title {
    font-size: 20px;
    line-height: 25px;
    font-weight: bold;
  }

  &__description {
    margin: 20px 0 0 0;
  }

  &__sentTAN {
    margin: 20px 0;
  }

  &__input {
    margin: 20px 0;
  }

  &__button {
    color: var(--c-original-green);
    display: block;
  }

  button {
    margin: 0 0 20px;
    min-width: 200px;
  }

  &__end {
    display: block;
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .auth {
  }
}
</style>
